import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { cn } from '@/core/utils/cn';
import { ChevronDownIcon } from '@/assets/icons/chevron-down.icon';
import { useState } from 'react';
import { getDateRangeFromLabel } from '@/core/utils/date/date-range';
import { useAnalytics } from '../../hooks/useAnalytics.hook';

const DATE_FILTER_OPTIONS = [
  'Today',
  'Yesterday',
  'Past 7 days',
  'Past 30 days',
  'Past 90 days',
  'This week',
  'This month',
  'Last week',
  'Last month',
];

type DatePreset = (typeof DATE_FILTER_OPTIONS)[number];

const DateFilter = () => {
  const { dateFilter, setDateFilter } = useAnalytics();
  const [selectedLabel, setSelectedLabel] = useState<DatePreset>('Past 30 days');

  const handleChange = (label: DatePreset) => {
    setSelectedLabel(label);

    const { start, end } = getDateRangeFromLabel(label);

    setDateFilter({
      start: start.toISOString(),
      end: end.toISOString(),
    });
  };

  return (
    <Listbox value={selectedLabel} onChange={handleChange}>
      <ListboxButton
        className={cn(
          'min-w-32 text-lighter relative block rounded-lg bg-surface-dark border border-secondary py-1.5 pr-8 pl-2 text-left text-sm/6',
          'focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25',
        )}
      >
        {new Intl.DateTimeFormat('en-US', {
          month: 'long',
          day: '2-digit',
          year: 'numeric',
        }).format(new Date(dateFilter.start))}{' '}
        -{' '}
        {new Intl.DateTimeFormat('en-US', {
          month: 'long',
          day: '2-digit',
          year: 'numeric',
        }).format(new Date(dateFilter.end))}
        <ChevronDownIcon className="group pointer-events-none absolute top-2.5 right-2.5 size-4" />
      </ListboxButton>
      <ListboxOptions
        anchor="bottom"
        transition
        className={cn(
          'min-w-32 z-10 w-[var(--button-width)] rounded-lg border border-secondary bg-surface-dark p-1 [--anchor-gap:var(--spacing-1)] focus:outline-none',
          'transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0',
        )}
      >
        {DATE_FILTER_OPTIONS.map((range: string) => (
          <ListboxOption
            key={range}
            value={range}
            className=" group flex cursor-default items-center gap-2 rounded-lg py-1.5 px-3 select-none data-[focus]:bg-dark"
          >
            <div className="text-sm/6 text-lighter">{range}</div>
          </ListboxOption>
        ))}
      </ListboxOptions>
    </Listbox>
  );
};

export default DateFilter;
